import { useEffect, useState, type ReactNode } from 'react';
import { Avatar, Badge, Button, LogoMark, NavItem } from '@assist/ui';
import { useSession } from '../state/session.js';
import { usePeriode } from '../state/periode.js';
import { endpoints } from '../state/api.js';
import { labelPeriode, periodeDitampilkan, rutePakaiPeriode } from '../lib/periode.js';
import { lencanaNotifikasi } from '../lib/notifikasi.js';
import { Icon } from './Icon.js';

/**
 * Kerangka konsol: sidebar, bilah atas, blok pengguna.
 *
 * ★ SIDEBAR DIBANGUN DARI MANIFEST. Tidak ada satu pun nama menu yang diketik di
 * berkas ini. Isinya datang dari `GET /v1/me/nav`, yang disusun Registry dari
 * `provides.nav` ketujuh manifest modul, disaring entitlement dan peran.
 *
 * Akibatnya tiga hal yang dijanjikan peta §7 berlaku tanpa usaha tambahan:
 *   · modul yang tidak dibeli TIDAK TERLIHAT SAMA SEKALI — bukan menu mati;
 *   · kelompok yang menjadi kosong ikut hilang (IMPROVEMENT dan KNOWLEDGE
 *     memang tidak muncul di tahap 1, karena modul pemiliknya belum ada);
 *   · menyalakan modul pada tenant berjalan mengubah menu dalam hitungan menit,
 *     tanpa penggelaran.
 *
 * Tiga hal yang peta sengaja TIDAK beri slot navigasi diperlakukan sama di sini:
 * pengaturan akun lewat blok pengguna di kaki sidebar, pencarian global lewat
 * kotak cari di bilah atas, dan eskalasi lewat layar Meetings.
 */
export function Shell({
  current,
  onNavigate,
  onSearch,
  children,
}: {
  current: string;
  onNavigate: (route: string) => void;
  /** Kotak cari bilah atas — peta §4 sengaja tidak memberinya slot navigasi. */
  onSearch: (query: string) => void;
  children: ReactNode;
}) {
  const { me, nav, locale, t, setLocale, signOut } = useSession();
  const [query, setQuery] = useState('');
  const { periode, setPeriode, pilihan } = usePeriode();

  /**
   * LENCANA NOTIFIKASI — Figma `107:220`, berbunyi `3` di setiap frame.
   *
   * TEMUAN PEMILIK PROYEK, 28 September 2026: ia digambar di 28 frame dan tidak pernah
   * ditulis. Ia dipasang di sini dengan RUTENYA, bukan sebagai angka hias — lihat
   * `GET /v1/notifications` di `modules/M01/src/routes/me.ts`, yang sampai hari ini
   * terdaftar di manifest dan menjawab `501`.
   *
   * `null` berarti BELUM DIMUAT dan ditampilkan sebagai `—`, bukan `0`. Bedanya bukan
   * kosmetik: `0` menyatakan "tidak ada yang menunggu Anda", dan menyatakannya sebelum
   * jawabannya tiba adalah kalimat yang belum tentu benar — kelas cacat yang sama dengan
   * `metric_item_daily` kosong yang digambar sebagai garis nol (lihat `analytics.ts`).
   */
  const [belumTerbaca, setBelumTerbaca] = useState<number | null>(null);
  useEffect(() => {
    let batal = false;
    endpoints
      .notifications()
      .then((r) => {
        if (!batal) setBelumTerbaca(r.unread_count);
      })
      .catch(() => {
        // Diam dengan sengaja: lencana yang gagal dimuat tetap `—`, dan sebuah kesalahan
        // memuat notifikasi tidak boleh menutupi layar yang sedang dikerjakan orang.
      });
    return () => {
      batal = true;
    };
  }, [current]);

  /**
   * PEMILIH PERIODE — Figma `5:58`, berbunyi `September 2026 ▾` di setiap frame KONSOL TENANT.
   *
   * TIGA KEADAAN, dan yang ketiga ditambahkan cacat 2 (28 September 2026)
   * --------------------------------------------------------------------
   * TEMUAN PEMILIK PROYEK yang kedua, dan yang paling mudah dipasang secara salah: sebuah
   * pil yang dapat dibuka dan tidak mengubah apa pun. `tools/ci/gerbang-janji-kendali.mjs`
   * dipasang justru untuk melarang bentuk itu.
   *
   *   · HIDUP  — di tiga layar yang rutenya benar-benar menerima periode
   *              (`apps/console/src/lib/periode.ts` menyebut ketiganya beserta rutenya).
   *   · MATI   — di layar KONSOL TENANT lainnya, dengan keterangan yang berdiri tanpa perlu
   *              ditekan. Dimatikan dan bukan disembunyikan, karena di konsol tenant ia
   *              KADANG aktif: bilah atas yang berubah bentuk dari layar ke layar bukan yang
   *              digambar Figma, dan keterangan yang terbaca tanpa interaksi menjelaskan
   *              lebih banyak daripada kendali yang hilang tanpa kata.
   *   · TIDAK  — di KONSOL PLATFORM, tempat ia TIDAK PERNAH aktif. Aturan "mati dengan
   *     ADA      keterangan" dibuat untuk keadaan "kadang"; di sana yang tersisa hanyalah
   *              kendali mati permanen ditambah satu baris teks yang menggantung di kelima
   *              layarnya, dan tidak satu pun frame platform menggambar keduanya.
   */
  const periodeTampil = periodeDitampilkan(current);
  const periodeAktif = periodeTampil && rutePakaiPeriode(current);

  /**
   * LENCANA NOTIFIKASI — cacat 3. Keputusan "kapan ia TIDAK dirender" ada di
   * `apps/console/src/lib/notifikasi.ts` dan bukan di dalam JSX di bawah, karena keadaan
   * NOL tidak dapat diuji dari `renderToStaticMarkup`: `useEffect` tidak berjalan di sana,
   * jadi hitungannya selamanya `null`. Sebuah keputusan yang hanya hidup di dalam JSX hanya
   * dapat diuji oleh peramban, dan gerbang yang menuntut peramban ditemui terlambat.
   */
  const lencana = lencanaNotifikasi(belumTerbaca);

  return (
    <div className="shell">
      <a className="shell__skip" href="#main">
        {t('app.skipToContent')}
      </a>

      <aside className="shell__sidebar">
        <div className="shell__brand">
          {/*
            AUDIT S-01 (BERAT). Di sini pernah berdiri `<span className="shell__brand-mark">A</span>` —
            kotak navy berisi huruf "A" — di seluruh 28 layar, termasuk layar Masuk. Komponen
            `Logo / Tanda` ada di Figma (`49:126`) dan dipakai di SETIAP frame. Identitas merek
            yang hilang di setiap layar bukan detail yang meleset; ia hal pertama yang dilihat mata.
          */}
          <span className="shell__brand-logo" data-figma="shell.logo">
            <LogoMark size={32} />
          </span>
          <span className="shell__brand-text" data-figma="shell.merek">
            <strong>{t('app.name')}</strong>
            <small>{t('app.byPqm')}</small>
          </span>
        </div>

        <nav className="shell__nav" aria-label={t('app.mainNavigation')} data-figma="shell.nav">
          {(nav?.groups ?? []).map((group) => (
            <div key={group.group} className="shell__nav-group">
              <h2 className="shell__nav-heading">{t(`nav.group.${group.group}`)}</h2>
              {group.items.map((item) => (
                <NavItem
                  key={item.route}
                  label={locale === 'id-ID' ? item.label_id : item.label_en}
                  icon={<Icon name={item.icon} />}
                  active={current === item.route || current.startsWith(`${item.route}/`)}
                  /*
                    Lencana `107:218`. Angkanya datang dari `GET /v1/me/nav`, yang
                    membacanya dari manifest butir itu sendiri (`badge`) — bukan dari
                    daftar kedua di sini. Butir yang manifestnya tidak menyatakan `badge`
                    tidak pernah menerima angka, dan karena itu tidak pernah menampilkan
                    lencana kosong.
                  */
                  badge={item.badge_count}
                  onSelect={() => onNavigate(item.route)}
                />
              ))}
            </div>
          ))}
        </nav>

        {/*
          Blok pengguna — PINTU MASUK `/settings/account`, yang peta §4 sengaja tidak beri
          slot navigasi. Sebelumnya blok ini hanya menampilkan nama dan tombol keluar, jadi
          pintu itu tidak ada sama sekali: satu-satunya cara mencapai pengaturan akun adalah
          mengetik alamatnya. Nama pengguna kini TOMBOL, dan itu yang peta maksud.
        */}
        {/*
          AUDIT S-05 (BERAT) dan K-08. Tiga perubahan, dan ketiganya diukur dari tangkapan
          layar yang sama (`02-dashboard-en.png`, sudut kiri bawah):

            · avatar 36 px → 32 px. Tangga Figma tepat tiga — 26 / 32 / 40 — dan 36 bukan
              salah satunya. Empat piksel itu yang menyisakan lebar terlalu sempit bagi nama.
            · `shell__user-hint` ("Account settings") DIHAPUS. Namanya sendiri sudah menjadi
              tombol menuju pengaturan akun; barisnya adalah baris ketiga yang menabrak
              "Sign out". Judul tombol tetap menyatakan tujuannya lewat `title`, jadi
              pintunya tidak hilang — yang hilang hanya baris yang mengulanginya.
            · "Keluar" pindah ke BARISNYA SENDIRI. Figma `5:2` tidak menaruh tautan keluar
              di blok ini sama sekali; menaruhnya di bawah adalah kompromi yang tidak
              mengorbankan namanya.

          Sesudah Inter di-self-host (F-03), "Sari Rahayu" muat penuh dalam 248 px.
        */}
        <div className="shell__user" data-figma="shell.pengguna">
          <div className="shell__user-row">
            <Avatar name={me?.full_name ?? '?'} size={32} />
            <button
              type="button"
              className="shell__user-text shell__user-button"
              title={t('app.accountSettings')}
              aria-current={current === '/settings/account' ? 'page' : undefined}
              onClick={() => onNavigate('/settings/account')}
            >
              <strong>{me?.full_name}</strong>
              <small>{me?.role_code ? t(`role.${me.role_code}`) : (me?.actor_kind ?? '')}</small>
            </button>
          </div>
          <Button variant="text" className="shell__user-signout" onClick={() => void signOut()}>
            {t('app.signOut')}
          </Button>
        </div>
      </aside>

      <div className="shell__main">
        <header className="shell__topbar">
          {/*
            KOTAK CARI YANG BENAR-BENAR MENCARI. Sebelumnya ia `<input>` tanpa `value`,
            tanpa `onChange`, dan tanpa formulir — sebuah kendali yang menerima ketukan dan
            tidak melakukan apa pun, di tempat yang paling terlihat di seluruh konsol.
            `GET /v1/search` ada; yang tidak ada adalah pemanggilnya (kelas cacat D-02).

            Ia `<form>` supaya Enter bekerja, dan ia TIDAK mengirim per ketukan: kueri di
            bawah tiga karakter ditolak server, dan mengirimnya tiga kali sebelum kata
            pertama selesai adalah tiga pemindaian indeks yang tidak berguna.
          */}
          <form
            className="shell__search"
            data-figma="shell.cari"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              if (query.trim().length >= 3) onSearch(query.trim());
            }}
          >
            <label className="shell__search-label">
              <span className="assist-sr-only">{t('app.search.label')}</span>
              <Icon name="search" />
              <input
                type="search"
                value={query}
                placeholder={t('app.search.placeholder')}
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
            <button type="submit" className="assist-sr-only">
              {t('app.search.label')}
            </button>
          </form>

          {/* Pengalih bahasa. Berlaku SEKETIKA, tidak memuat ulang halaman, dan
              tidak menghilangkan isian yang belum disimpan (peta §7). */}
          <div className="shell__lang" data-figma="shell.bahasa" role="group" aria-label={t('app.language')}>
            {/* Ikon globe 16 px di depan label — Figma `70:1445` (audit K-10). */}
            <span className="shell__lang-globe" aria-hidden="true">
              <Icon name="globe" size={16} />
            </span>
            <button
              type="button"
              className={locale === 'en-US' ? 'is-active' : ''}
              aria-pressed={locale === 'en-US'}
              onClick={() => void setLocale('en-US')}
            >
              EN
            </button>
            <button
              type="button"
              className={locale === 'id-ID' ? 'is-active' : ''}
              aria-pressed={locale === 'id-ID'}
              onClick={() => void setLocale('id-ID')}
            >
              ID
            </button>
          </div>

          {/*
            PEMILIH PERIODE `5:58`. `<select>` asli dan bukan menu buatan sendiri: dua
            belas pilihan tidak menuntut popup, dan `<select>` sudah membawa keyboard,
            pembaca layar, dan perilaku ponsel yang benar tanpa satu baris pun kode.
            Bentuk pil dan tanda `▾` ditangani `.shell__periode` di `console.css`.
          */}
          {periodeTampil ? (
            <div className="shell__periode-blok">
              <label className="assist-sr-only" htmlFor="shell-periode">
                {t('app.period.label')}
              </label>
              <select
                id="shell-periode"
                className="shell__periode"
                data-figma="shell.periode"
                disabled={!periodeAktif}
                value={periode}
                onChange={(e) => setPeriode(e.target.value)}
                aria-describedby={periodeAktif ? undefined : 'shell-periode-catatan'}
              >
                {pilihan.map((p) => (
                  <option key={p} value={p}>
                    {labelPeriode(p, locale)}
                  </option>
                ))}
              </select>
              {periodeAktif ? null : (
                <span className="shell__periode-catatan" id="shell-periode-catatan">
                  {t('app.period.notApplicable')}
                </span>
              )}
            </div>
          ) : null}

          {/*
            LENCANA NOTIFIKASI `107:220`. Di Figma ia INSTANCE `Badge` bernada neutral —
            bukan tombol, bukan tautan. Ia karena itu dirender sebagai BACAAN, dan tidak
            ada yang perlu terjadi ketika ia ditekan: memasang tombol yang membuka layar
            yang belum dirancang adalah kelas cacat yang sedang diperbaiki pekerjaan ini,
            bukan yang ditambahkan olehnya.

            CACAT 3, 28 September 2026. Figma menggambar `3`; yang berdiri di produksi
            adalah lingkaran berisi `0` di setiap layar. Lingkaran berisi nol menempati
            ruang bilah atas untuk mengatakan "tidak ada apa-apa" — yang sudah dikatakan
            lebih baik, dan tanpa ruang sama sekali, oleh tidak adanya lencana. Ia juga
            MELATIH mata mengabaikannya, sehingga ketika angkanya kelak menjadi `3` ia tidak
            lagi menarik perhatian.

            Keadaan `—` DIPERTAHANKAN (PENERJEMAHAN-DESAIN.md §2.3): sebelum jawabannya
            tiba, "nol" dan "belum tahu" adalah dua hal berbeda, dan `0` selama pemuatan
            berbohong seperempat detik pada setiap perpindahan layar.

            Teks pembaca layar ikut hilang bersamanya, dan itu disengaja: "tidak ada
            notifikasi belum terbaca" yang diumumkan pada SETIAP layar adalah kebisingan
            yang sama, hanya untuk telinga alih-alih mata.
          */}
          {lencana.tampil ? (
            <span className="shell__notifikasi" data-figma="shell.notifikasi">
              <Badge tone="neutral">{lencana.teks}</Badge>
              <span className="assist-sr-only">
                {belumTerbaca === null
                  ? t('app.notifications.loading')
                  : t('app.notifications.unread', { count: belumTerbaca })}
              </span>
            </span>
          ) : null}

          {/*
            PENYIMPANGAN TERCATAT V-01 — nama organisasi di bilah atas TIDAK ada di Figma.
            Figma menaruhnya di subjudul Dasbor (`5:69`), dan untuk pengguna tenant itu
            memang cukup: organisasinya satu dan tidak pernah berubah.

            Ia DIPERTAHANKAN untuk aktor yang konteksnya DAPAT berpindah — konsultan PQM
            yang sedang mendampingi klien, dan peran platform. Bagi mereka satu-satunya
            jawaban atas "organisasi siapa yang sedang saya lihat" tidak boleh berada di
            satu layar saja, karena kesalahan yang dicegahnya adalah menulis sesuatu ke
            organisasi yang salah. Bagi pengguna tenant (`actor_kind = 'TENANT'`) ia sekarang HILANG, dan dengan
            begitu bilah atasnya sama persis dengan yang digambar 28 frame.
          */}
          {me?.tenant && me.actor_kind !== 'TENANT' ? (
            <span className="shell__tenant">{me.tenant.name}</span>
          ) : null}
        </header>

        <main id="main" className="shell__content">
          {children}
        </main>
      </div>
    </div>
  );
}
